import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, setDoc, doc, query, where,
  onSnapshot, serverTimestamp, orderBy, writeBatch
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import LoadingSpinner from './components/LoadingSpinner';
import './MessagingApp.css';

function MessagingApp() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  // Stable conversation identity — store id + isGroup, look up the rest from `conversations`/`users`.
  // This avoids the {...conv, isGroup: true} pattern from Build 22 that re-created the
  // selected-user object on every conversations snapshot, causing the messages effect
  // to re-fire and the unread-badge to flicker.
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedIsGroup, setSelectedIsGroup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]); // DMs to me — for unread DM counts
  const [groupMessages, setGroupMessages] = useState([]); // Group msgs in my conversations — for unread group counts
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]);
  const [groupName, setGroupName] = useState('');

  // Track which DM message IDs we've already marked read this session, so the
  // snapshot listener doesn't keep re-firing writes (root cause of the flicker).
  const markedReadRef = useRef(new Set());
  // Throttle group lastReadAt writes so rapid snapshot churn doesn't spam Firestore.
  const lastGroupReadBumpRef = useRef({});

  const role = userProfile?.role || null;

  // Resolve currently-selected conversation/user from id (stable lookup, no re-renders triggered)
  const selectedUser = (() => {
    if (!selectedConversationId) return null;
    if (selectedIsGroup) {
      const conv = conversations.find(c => c.id === selectedConversationId);
      return conv ? { ...conv, isGroup: true } : null;
    }
    return users.find(u => u.id === selectedConversationId) || null;
  })();

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Messaging - Wixon & Cross Desk Suite';
  }, []);

  // Users listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, 'users'), snap => {
      const allUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(allUsers.filter(u => u.id !== user.uid));
    });
    return () => unsub();
  }, [user]);

  // Conversations listener — sorted desc by lastMessageTime (most recent first)
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    const unsub = onSnapshot(q, snap => {
      const convos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      convos.sort((a, b) =>
        (b.lastMessageTime?.toMillis?.() || 0) - (a.lastMessageTime?.toMillis?.() || 0)
      );
      setConversations(convos);
    });

    return () => unsub();
  }, [user]);

  // DM inbox listener (for unread badge per DM)
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'messages'),
      where('toUserId', '==', user.uid)
    );

    const unsub = onSnapshot(q, snap => {
      setInboxMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, [user]);

  // Group messages listener (for unread badge per group)
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      where('isGroup', '==', true)
    );

    const unsub = onSnapshot(q, snap => {
      setGroupMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, [user]);

  // Active-conversation messages listener
  useEffect(() => {
    if (!user || !selectedUser) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'asc')
    );

    const unsub = onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const filtered = selectedUser.isGroup
        ? all.filter(msg => msg.conversationId === selectedUser.id)
        : all.filter(msg =>
            (msg.fromUserId === user.uid && msg.toUserId === selectedUser.id) ||
            (msg.fromUserId === selectedUser.id && msg.toUserId === user.uid)
          );

      setMessages(filtered);
    });

    return () => unsub();
    // Stable deps — id + isGroup, not the full object. Prevents re-fire when
    // a sibling listener mutates the conversations array.
  }, [user, selectedConversationId, selectedIsGroup]);

  // Mark messages read when a conversation is opened.
  // The previous design fired updateDoc inside the snapshot callback for every
  // new snapshot, which produced its own snapshot, which produced the flicker.
  // Now: dedupe via a ref of message-IDs we've already written, and use a single
  // batch write per pending unread set.
  useEffect(() => {
    if (!user || !selectedUser) return;

    if (!selectedUser.isGroup) {
      const unread = inboxMessages.filter(
        msg => msg.fromUserId === selectedUser.id && !msg.read
      );
      if (unread.length === 0) return;

      const toWrite = unread.filter(msg => !markedReadRef.current.has(msg.id));
      if (toWrite.length === 0) return;

      toWrite.forEach(msg => markedReadRef.current.add(msg.id));

      const batch = writeBatch(db);
      toWrite.forEach(msg => {
        batch.update(doc(db, 'messages', msg.id), { read: true });
      });
      batch.commit().catch(err => {
        console.error('Failed to mark DMs read:', err);
        // Roll back the dedupe set on failure so a retry can happen
        toWrite.forEach(msg => markedReadRef.current.delete(msg.id));
      });
      return;
    }

    // Group: bump this user's lastReadAt on the conversation document.
    // Throttle to once every ~2s per group so rapid snapshot churn doesn't cause writes.
    const now = Date.now();
    const last = lastGroupReadBumpRef.current[selectedUser.id] || 0;
    if (now - last < 2000) return;
    lastGroupReadBumpRef.current[selectedUser.id] = now;

    updateDoc(doc(db, 'conversations', selectedUser.id), {
      [`lastReadAt.${user.uid}`]: serverTimestamp()
    }).catch(err => {
      console.error('Failed to update group lastReadAt:', err);
      lastGroupReadBumpRef.current[selectedUser.id] = 0; // allow retry
    });
  }, [user, selectedUser, inboxMessages, messages]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const getConversationId = (userId1, userId2) => {
    return [userId1, userId2].sort().join('_');
  };

  const formatDateSeparator = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const msgDate = new Date(date);

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    msgDate.setHours(0, 0, 0, 0);

    if (msgDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (msgDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const shouldShowDateSeparator = (currentMsg, previousMsg) => {
    if (!previousMsg) return true;

    const currentDate = currentMsg.createdAt?.toDate?.();
    const previousDate = previousMsg.createdAt?.toDate?.();

    if (!currentDate || !previousDate) return false;

    const current = new Date(currentDate);
    const previous = new Date(previousDate);
    current.setHours(0, 0, 0, 0);
    previous.setHours(0, 0, 0, 0);

    return current.getTime() !== previous.getTime();
  };

  const handleCreateGroup = async () => {
    if (selectedGroupUsers.length < 2) {
      alert('Please select at least 2 users for a group');
      return;
    }
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    try {
      const allParticipants = [user.uid, ...selectedGroupUsers];
      const participantEmails = [
        user.email,
        ...selectedGroupUsers.map(uid => users.find(u => u.id === uid)?.email)
      ].filter(Boolean);

      // Initialize lastReadAt for everyone to "now" so the new group doesn't
      // immediately show as unread for participants.
      const lastReadAt = {};
      allParticipants.forEach(uid => { lastReadAt[uid] = serverTimestamp(); });

      await addDoc(collection(db, 'conversations'), {
        isGroup: true,
        groupName: groupName,
        participants: allParticipants,
        participantEmails: participantEmails,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        lastMessage: 'Group created',
        lastMessageTime: serverTimestamp(),
        lastReadAt
      });

      setShowGroupModal(false);
      setSelectedGroupUsers([]);
      setGroupName('');
      alert('Group created!');
    } catch (err) {
      console.error('Error creating group:', err);
      alert('Error creating group');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedUser) return;

    try {
      if (selectedUser.isGroup) {
        await addDoc(collection(db, 'messages'), {
          conversationId: selectedUser.id,
          fromUserId: user.uid,
          fromUserEmail: user.email,
          content: messageText,
          isGroup: true,
          participants: selectedUser.participants,
          createdAt: serverTimestamp()
        });

        await updateDoc(doc(db, 'conversations', selectedUser.id), {
          lastMessage: messageText,
          lastMessageTime: serverTimestamp(),
          [`lastReadAt.${user.uid}`]: serverTimestamp()
        });
      } else {
        const conversationId = getConversationId(user.uid, selectedUser.id);

        await addDoc(collection(db, 'messages'), {
          fromUserId: user.uid,
          fromUserEmail: user.email,
          toUserId: selectedUser.id,
          toUserEmail: selectedUser.email,
          content: messageText,
          read: false,
          participants: [user.uid, selectedUser.id],
          createdAt: serverTimestamp()
        });

        const convRef = doc(db, 'conversations', conversationId);
        await setDoc(convRef, {
          isGroup: false,
          participants: [user.uid, selectedUser.id],
          participantEmails: [user.email, selectedUser.email],
          lastMessage: messageText,
          lastMessageTime: serverTimestamp()
        }, { merge: true });
      }

      setMessageText('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message');
    }
  };

  const getDmUnreadCount = (otherUserId) => {
    return inboxMessages.filter(msg =>
      msg.fromUserId === otherUserId && !msg.read
    ).length;
  };

  const getGroupUnreadCount = (conv) => {
    if (!conv?.isGroup) return 0;
    const lastReadTs = conv.lastReadAt?.[user.uid];
    const lastReadMs = lastReadTs?.toMillis?.() ?? 0;
    return groupMessages.filter(msg =>
      msg.conversationId === conv.id &&
      msg.fromUserId !== user.uid && // never count own messages as unread
      (msg.createdAt?.toMillis?.() ?? 0) > lastReadMs
    ).length;
  };

  // ── Sidebar data: build sorted, recency-aware lists ──────────────────────────

  // Groups: from conversations, already sorted desc by lastMessageTime
  const groupConversations = conversations.filter(c => c.isGroup);

  // DMs: only conversations where I have message history (per Build 23 requirement).
  // Pair each conversation with the "other user" record for rendering.
  const dmConversations = conversations
    .filter(c => !c.isGroup)
    .map(conv => {
      const otherUserId = conv.participants?.find(p => p !== user?.uid);
      const otherUser = users.find(u => u.id === otherUserId);
      if (!otherUser) return null; // user might be deleted
      return { conv, otherUser };
    })
    .filter(Boolean);

  // Apply search filter
  const q = searchQuery.trim().toLowerCase();
  const filteredGroups = q
    ? groupConversations.filter(c => c.groupName?.toLowerCase().includes(q))
    : groupConversations;
  const filteredDms = q
    ? dmConversations.filter(({ otherUser }) =>
        otherUser.email.toLowerCase().includes(q)
      )
    : dmConversations;

  // For starting brand-new conversations, surface users with no history
  // *only* when actively searching, so the DM list stays recency-sorted.
  const usersWithNoHistory = q
    ? users.filter(u =>
        u.email.toLowerCase().includes(q) &&
        !dmConversations.some(({ otherUser }) => otherUser.id === u.id)
      )
    : [];

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  return (
    <div className="messaging-app">
      <header className="header">
        <div className="header-content">
          <div>
            <div className="breadcrumb-nav-app">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
                📦 Dashboard
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">💬 Messaging</span>
            </div>
          </div>
          <div className="header-actions">
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="messaging-main">
        <div className="messaging-container">
          {/* Sidebar - Conversations */}
          <div className="messaging-sidebar">
            <div className="sidebar-header">
              <div className="sidebar-header-top">
                <h2>Conversations</h2>
                <button className="btn btn-primary btn-new-group" onClick={() => setShowGroupModal(true)}>
                  + New Group
                </button>
              </div>
              <input
                type="text"
                placeholder="Search conversations or users..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="sidebar-content">
              {/* Groups Section */}
              {filteredGroups.length > 0 && (
                <div className="conversations-section">
                  <div className="section-label">Groups</div>
                  {filteredGroups.map(conv => {
                    const unread = getGroupUnreadCount(conv);
                    const isSelected = selectedConversationId === conv.id && selectedIsGroup;
                    return (
                      <div
                        key={conv.id}
                        className={`user-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedConversationId(conv.id);
                          setSelectedIsGroup(true);
                        }}
                      >
                        <div className="user-avatar">👥</div>
                        <div className="user-info">
                          <div className="user-name">{conv.groupName}</div>
                          <div className="user-role">{conv.participants.length} members</div>
                        </div>
                        {unread > 0 && (
                          <div className="unread-badge">{unread}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Direct Messages Section — sorted by recency, only users I've messaged */}
              <div className="conversations-section">
                <div className="section-label">Direct Messages</div>
                <div className="user-list">
                  {filteredDms.map(({ conv, otherUser }) => {
                    const unread = getDmUnreadCount(otherUser.id);
                    const isSelected = selectedConversationId === otherUser.id && !selectedIsGroup;
                    return (
                      <div
                        key={otherUser.id}
                        className={`user-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedConversationId(otherUser.id);
                          setSelectedIsGroup(false);
                        }}
                      >
                        <div className="user-avatar">{otherUser.email[0].toUpperCase()}</div>
                        <div className="user-info">
                          <div className="user-name">{otherUser.email}</div>
                          <div className="user-role">{otherUser.role}</div>
                        </div>
                        {unread > 0 && (
                          <div className="unread-badge">{unread}</div>
                        )}
                      </div>
                    );
                  })}
                  {filteredDms.length === 0 && !q && (
                    <div className="empty-state-sidebar">
                      No conversations yet. Search to start a new one.
                    </div>
                  )}
                </div>
              </div>

              {/* Search-only: users with no history yet */}
              {usersWithNoHistory.length > 0 && (
                <div className="conversations-section">
                  <div className="section-label">Start new conversation</div>
                  <div className="user-list">
                    {usersWithNoHistory.map(u => (
                      <div
                        key={u.id}
                        className={`user-item ${selectedConversationId === u.id && !selectedIsGroup ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedConversationId(u.id);
                          setSelectedIsGroup(false);
                        }}
                      >
                        <div className="user-avatar">{u.email[0].toUpperCase()}</div>
                        <div className="user-info">
                          <div className="user-name">{u.email}</div>
                          <div className="user-role">{u.role}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {q && filteredGroups.length === 0 && filteredDms.length === 0 && usersWithNoHistory.length === 0 && (
                <div className="empty-state-sidebar">No matches</div>
              )}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="messaging-content">
            {selectedUser ? (
              <>
                <div className="chat-header">
                  <div className="chat-user-info">
                    <div className="chat-avatar">
                      {selectedUser.isGroup ? '👥' : selectedUser.email[0].toUpperCase()}
                    </div>
                    <div>
                      <h3>{selectedUser.isGroup ? selectedUser.groupName : selectedUser.email}</h3>
                      <span className="chat-role">
                        {selectedUser.isGroup ? `${selectedUser.participants.length} members` : selectedUser.role}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="messages-container">
                  {messages.length === 0 ? (
                    <div className="empty-state-chat">
                      <p>No messages yet. Start a conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isOwn = msg.fromUserId === user.uid;
                      const previousMsg = index > 0 ? messages[index - 1] : null;
                      const showDateSeparator = shouldShowDateSeparator(msg, previousMsg);

                      return (
                        <React.Fragment key={msg.id}>
                          {showDateSeparator && msg.createdAt?.toDate && (
                            <div className="date-separator">
                              <span>{formatDateSeparator(msg.createdAt.toDate())}</span>
                            </div>
                          )}
                          <div className={`message ${isOwn ? 'own' : 'other'}`}>
                            <div className="message-bubble">
                              {selectedUser.isGroup && !isOwn && (
                                <div className="message-sender">{msg.fromUserEmail}</div>
                              )}
                              <div className="message-content">{msg.content}</div>
                              <div className="message-time">
                                {msg.createdAt?.toDate?.().toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) || 'Sending...'}
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} className="message-input">
                  <input
                    type="text"
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                  />
                  <button type="submit" disabled={!messageText.trim()}>
                    Send
                  </button>
                </form>
              </>
            ) : (
              <div className="empty-state-main">
                <h2>Select a conversation</h2>
                <p>Choose a conversation from the list to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Group Creation Modal */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create New Group</h2>
            <div className="form-group">
              <label>Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="Enter group name..."
              />
            </div>
            <div className="form-group">
              <label>Select Members ({selectedGroupUsers.length} selected)</label>
              <div className="user-selection-list">
                {users.map(u => (
                  <label key={u.id} className="user-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedGroupUsers.includes(u.id)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedGroupUsers([...selectedGroupUsers, u.id]);
                        } else {
                          setSelectedGroupUsers(selectedGroupUsers.filter(id => id !== u.id));
                        }
                      }}
                    />
                    <span>{u.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleCreateGroup}>
                Create Group
              </button>
              <button className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagingApp;
